import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CellInput } from "@/components/ui/cell-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { BarChart3, Save, X, ChevronDown, ChevronUp, TrendingUp, TrendingDown, AlertTriangle, Search, Zap, Check, XCircle, Trash2, RefreshCw, Settings2, ArrowRight } from "lucide-react";
import { useCurrency } from "@/lib/currency";

const ROLES = ["Director", "Associate", "Senior Engineer", "Engineer", "CAD/BIM Technician"];

function ActualsEditor({ benchmark, onSave, onCancel }) {
  const cur = useCurrency();
  const [form, setForm] = useState({
    actual_total_hours: benchmark.actual_total_hours || 0,
    actual_hours_by_role: benchmark.actual_hours_by_role || Object.fromEntries(ROLES.map(r => [r, 0])),
    actual_hours_by_stage: benchmark.actual_hours_by_stage || {},
    actual_hours_by_discipline: benchmark.actual_hours_by_discipline || {},
    actual_cost: benchmark.actual_cost || 0,
    actual_fee: benchmark.actual_fee || 0,
    actual_construction_value: benchmark.actual_construction_value || 0,
    actual_start_date: benchmark.actual_start_date || "",
    actual_end_date: benchmark.actual_end_date || "",
    lessons_learned: benchmark.lessons_learned || "",
    completion_status: benchmark.completion_status || "",
  });
  const [saving, setSaving] = useState(false);

  const updateRole = (role, val) => setForm(p => ({ ...p, actual_hours_by_role: { ...p.actual_hours_by_role, [role]: parseFloat(val) || 0 } }));
  const recalcTotal = () => {
    const total = Object.values(form.actual_hours_by_role).reduce((s, v) => s + (v || 0), 0);
    setForm(p => ({ ...p, actual_total_hours: total }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 border-t border-slate-800 pt-4 mt-4" data-testid="actuals-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-100">Record Actuals — {benchmark.name}</h3>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 text-xs border-slate-800"><X className="w-3 h-3 mr-1" />Cancel</Button>
      </div>

      {/* Hours by Role */}
      <Card className="bg-slate-800/50 border border-slate-800">
        <CardHeader className="py-2 px-4 border-b border-slate-800"><CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actual Hours by Role</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {ROLES.map(role => (
            <div key={role}>
              <Label className="text-xs text-slate-500">{role}</Label>
              <CellInput type="number" value={form.actual_hours_by_role[role] || ""} onChange={(e) => updateRole(role, e.target.value)} onBlur={recalcTotal} className="mt-1 h-8 font-mono text-xs bg-slate-900 border-slate-800" data-testid={`actual-hours-${role.split(" ")[0].toLowerCase()}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs font-semibold text-slate-500">Total Actual Hours</Label>
          <CellInput type="number" value={form.actual_total_hours} onChange={(e) => setForm(p => ({ ...p, actual_total_hours: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 font-mono text-xs bg-slate-900 border-slate-800" data-testid="actual-total-hours" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Actual Fee ({cur.symbol})</Label>
          <CellInput type="number" value={form.actual_fee || ""} onChange={(e) => setForm(p => ({ ...p, actual_fee: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 font-mono text-xs bg-slate-900 border-slate-800" data-testid="actual-fee" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Actual Cost ({cur.symbol})</Label>
          <CellInput type="number" value={form.actual_cost || ""} onChange={(e) => setForm(p => ({ ...p, actual_cost: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 font-mono text-xs bg-slate-900 border-slate-800" data-testid="actual-cost" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Actual CV ({cur.symbol})</Label>
          <CellInput type="number" value={form.actual_construction_value || ""} onChange={(e) => setForm(p => ({ ...p, actual_construction_value: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 font-mono text-xs bg-slate-900 border-slate-800" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-semibold text-slate-500">Start Date</Label>
          <Input type="date" value={form.actual_start_date || ""} onChange={(e) => setForm(p => ({ ...p, actual_start_date: e.target.value }))} className="mt-1 h-8 text-xs bg-slate-900 border-slate-800" data-testid="actual-start-date" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">End Date</Label>
          <Input type="date" value={form.actual_end_date || ""} onChange={(e) => setForm(p => ({ ...p, actual_end_date: e.target.value }))} className="mt-1 h-8 text-xs bg-slate-900 border-slate-800" data-testid="actual-end-date" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Completion Status</Label>
          <Select value={form.completion_status || ""} onValueChange={(v) => setForm(p => ({ ...p, completion_status: v }))}>
            <SelectTrigger className="mt-1 h-8 text-xs bg-slate-900 border-slate-800" data-testid="completion-status"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {["On Budget", "Under Budget", "Over Budget", "Significantly Over"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold text-slate-500">Lessons Learned</Label>
        <Textarea value={form.lessons_learned} onChange={(e) => setForm(p => ({ ...p, lessons_learned: e.target.value }))} rows={2} className="mt-1 text-sm bg-slate-900 border-slate-800" placeholder="Key learnings from this project..." data-testid="lessons-learned" />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-slate-800 text-white" data-testid="save-actuals-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Actuals"}
        </Button>
      </div>
    </div>
  );
}

/* ─── CALIBRATION TAB ─── */
function CalibrationTab() {
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/calibration/status");
      setStatus(res.data);
    } catch (e) {
      console.error("Failed to load calibration status", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await API.post("/calibration/toggle");
      setStatus(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(`Calibration engine ${res.data.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await API.post("/calibration/generate");
      toast.success(`Generated ${res.data.generated} recommendations from ${res.data.total_groups_analysed} groups`);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (recId) => {
    setActionLoading(prev => ({ ...prev, [recId]: "approve" }));
    try {
      await API.post(`/calibration/${recId}/approve`);
      toast.success("Recommendation approved — multiplier applied");
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setActionLoading(prev => ({ ...prev, [recId]: null }));
    }
  };

  const handleReject = async (recId) => {
    setActionLoading(prev => ({ ...prev, [recId]: "reject" }));
    try {
      await API.post(`/calibration/${recId}/reject`);
      toast.success("Recommendation rejected");
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setActionLoading(prev => ({ ...prev, [recId]: null }));
    }
  };

  const handleDelete = async (recId) => {
    setActionLoading(prev => ({ ...prev, [recId]: "delete" }));
    try {
      await API.delete(`/calibration/${recId}`);
      toast.success("Recommendation deleted");
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setActionLoading(prev => ({ ...prev, [recId]: null }));
    }
  };

  const handleClearAll = async () => {
    if (!(await confirm({ title: "Clear Calibration Data", description: "Clear all calibration recommendations? This cannot be undone.", confirmText: "Clear All", variant: "destructive" }))) return;
    setClearing(true);
    try {
      const res = await API.delete("/calibration/clear");
      toast.success(res.data.message);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={`skeleton-${i}`} className="h-20 bg-slate-800 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  const enabled = status?.enabled || false;
  const recs = status?.recommendations || [];
  const counts = status?.counts || { pending: 0, approved: 0, rejected: 0, total: 0 };
  const pending = recs.filter(r => r.status === "pending");
  const reviewed = recs.filter(r => r.status !== "pending");

  return (
    <div className="space-y-6" data-testid="calibration-tab">
      {/* Engine Toggle + Status */}
      <Card className="bg-slate-900 border border-slate-800 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${enabled ? "bg-emerald-500/10" : "bg-slate-800"}`}>
                <Settings2 className={`w-5 h-5 ${enabled ? "text-emerald-500" : "text-slate-500"}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100">Pricing Calibration Engine</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {enabled
                    ? "Analyses historical estimated vs actual hours to suggest complexity multiplier adjustments"
                    : "Enable to auto-calibrate pricing multipliers from historical project data"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{enabled ? "Enabled" : "Disabled"}</span>
              <Switch
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={toggling}
                data-testid="calibration-toggle"
                className={enabled ? "data-[state=checked]:bg-emerald-500" : ""}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      {enabled && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="generate-recommendations-btn"
            >
              <Zap className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Analysing..." : "Generate Recommendations"}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={loadStatus}
              className="border-slate-800 hover:bg-slate-800"
              data-testid="refresh-calibration-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          {counts.total > 0 && (
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={clearing}
              className="border-slate-800 hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/30 text-slate-400"
              data-testid="clear-all-calibration-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {clearing ? "Clearing..." : "Clear All"}
            </Button>
          )}
        </div>
      )}

      {/* KPI counters */}
      {enabled && counts.total > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Total", value: counts.total, color: "text-slate-100", bg: "bg-slate-800/50" },
            { label: "Pending", value: counts.pending, color: "text-amber-500", bg: "bg-amber-500/5" },
            { label: "Approved", value: counts.approved, color: "text-emerald-500", bg: "bg-emerald-500/5" },
            { label: "Rejected", value: counts.rejected, color: "text-rose-400", bg: "bg-rose-500/5" },
          ].map(k => (
            <Card key={k.label} className={`${k.bg} border border-slate-800 shadow-sm`} data-testid={`calibration-kpi-${k.label.toLowerCase()}`}>
              <CardContent className="p-3 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k.label}</p>
                <p className={`font-mono text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pending Recommendations */}
      {enabled && pending.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
          <CardHeader className="border-b border-slate-800 bg-amber-500/5 py-3 px-4">
            <CardTitle className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Pending Recommendations ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Scope</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Sector</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Type</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Discipline</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Sample</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Est. Hrs</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Act. Hrs</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Variance</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-center">Multiplier</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map(rec => {
                  const isOver = rec.variance_pct > 0;
                  const busy = !!actionLoading[rec.id];
                  return (
                    <TableRow key={rec.id} data-testid={`calibration-rec-${rec.id}`}>
                      <TableCell className="px-3 py-2.5">
                        <Badge variant="outline" className="text-[10px] font-mono border-slate-700 text-slate-400">
                          {rec.scope === "sector_discipline" ? "Sector+Disc" : "Sector+Type"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-300 font-medium">{rec.sector}</TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-400">{rec.project_type || "—"}</TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-400">{rec.discipline || "—"}</TableCell>
                      <TableCell className="px-3 py-2.5 font-mono text-xs text-right text-slate-400">{rec.sample_size}</TableCell>
                      <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{rec.total_estimated_hours}</TableCell>
                      <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{rec.total_actual_hours}</TableCell>
                      <TableCell className="px-3 py-2.5 text-right">
                        <span className={`inline-flex items-center gap-0.5 font-mono text-xs font-bold ${isOver ? "text-rose-400" : "text-emerald-400"}`}>
                          {isOver ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {isOver ? "+" : ""}{rec.variance_pct}%
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="font-mono text-xs text-slate-500">{rec.current_multiplier}x</span>
                          <ArrowRight className="w-3 h-3 text-slate-600" />
                          <span className={`font-mono text-xs font-bold ${isOver ? "text-rose-400" : "text-emerald-400"}`}>{rec.suggested_multiplier}x</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(rec.id)}
                            disabled={busy}
                            className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                            data-testid={`approve-rec-${rec.id}`}
                          >
                            {actionLoading[rec.id] === "approve" ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(rec.id)}
                            disabled={busy}
                            className="h-7 px-2.5 border-slate-700 hover:bg-rose-500/10 hover:text-rose-400 text-xs"
                            data-testid={`reject-rec-${rec.id}`}
                          >
                            {actionLoading[rec.id] === "reject" ? <RefreshCw className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(rec.id)}
                            disabled={busy}
                            className="h-7 px-2 text-slate-500 hover:text-rose-400"
                            data-testid={`delete-rec-${rec.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reviewed Recommendations (approved + rejected) */}
      {enabled && reviewed.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-sm font-semibold text-slate-100">
              Reviewed ({reviewed.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Status</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Sector</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Type</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Discipline</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Variance</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-center">Multiplier</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Reviewed</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewed.map(rec => {
                  const isApproved = rec.status === "approved";
                  const busy = !!actionLoading[rec.id];
                  return (
                    <TableRow key={rec.id} className={isApproved ? "bg-emerald-500/[0.02]" : "bg-rose-500/[0.02]"} data-testid={`calibration-reviewed-${rec.id}`}>
                      <TableCell className="px-3 py-2.5">
                        <Badge className={`text-[10px] ${isApproved ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}>
                          {rec.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-300 font-medium">{rec.sector}</TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-400">{rec.project_type || "—"}</TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-400">{rec.discipline || "—"}</TableCell>
                      <TableCell className="px-3 py-2.5 font-mono text-xs text-right">
                        <span className={rec.variance_pct > 0 ? "text-rose-400" : "text-emerald-400"}>
                          {rec.variance_pct > 0 ? "+" : ""}{rec.variance_pct}%
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="font-mono text-xs text-slate-500">{rec.current_multiplier}x</span>
                          <ArrowRight className="w-3 h-3 text-slate-600" />
                          <span className={`font-mono text-xs font-bold ${isApproved ? "text-emerald-400" : "text-slate-500 line-through"}`}>{rec.suggested_multiplier}x</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-xs text-slate-500">
                        {rec.reviewed_at ? new Date(rec.reviewed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(rec.id)}
                          disabled={busy}
                          className="h-7 px-2 text-slate-500 hover:text-rose-400"
                          data-testid={`delete-reviewed-${rec.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {enabled && counts.total === 0 && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardContent className="py-16 text-center">
            <Zap className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-slate-300">No Calibration Recommendations Yet</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Click "Generate Recommendations" to analyse historical project data. The engine compares estimated vs actual hours and suggests multiplier adjustments where variance exceeds 10%.
            </p>
            <p className="text-xs text-slate-600 mt-3">
              Requires projects with both pricing data and recorded actuals.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Disabled State */}
      {!enabled && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardContent className="py-16 text-center">
            <Settings2 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-slate-400">Calibration Engine Disabled</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Enable the toggle above to start auto-calibrating your pricing multipliers based on historical estimated vs actual hours data.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Benchmarking() {
  const cur = useCurrency();
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("projects");
  const [sectorAvgs, setSectorAvgs] = useState([]);
  const [sizeBenchmarks, setSizeBenchmarks] = useState([]);
  const [varianceReport, setVarianceReport] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [predFilters, setPredFilters] = useState({ sector: "", project_type: "", floor_area: "" });
  const [expandedRow, setExpandedRow] = useState(null);

  const loadBenchmarks = async () => {
    setLoading(true);
    try {
      const res = await API.get("/benchmarking");
      setBenchmarks(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBenchmarks(); }, []);

  const loadAnalytics = async () => {
    const [s, sz, v] = await Promise.all([
      API.get("/analytics/sector-averages").catch(() => ({ data: [] })),
      API.get("/analytics/size-benchmarks").catch(() => ({ data: [] })),
      API.get("/analytics/variance-report").catch(() => ({ data: null })),
    ]);
    setSectorAvgs(s.data);
    setSizeBenchmarks(sz.data);
    setVarianceReport(v.data);
  };

  const runPrediction = async () => {
    const params = new URLSearchParams();
    if (predFilters.sector) params.set("sector", predFilters.sector);
    if (predFilters.project_type) params.set("project_type", predFilters.project_type);
    if (predFilters.floor_area) params.set("floor_area", predFilters.floor_area);
    try {
      const res = await API.get(`/analytics/prediction?${params.toString()}`);
      setPrediction(res.data);
    } catch { toast.error("Prediction failed"); }
  };

  const saveActuals = async (projectId, data) => {
    try {
      await API.put(`/benchmarking/${projectId}/actuals`, data);
      toast.success("Actuals saved");
      setEditingId(null);
      loadBenchmarks();
    } catch { toast.error("Failed to save actuals"); }
  };

  const withPricing = benchmarks.filter(b => b.total_fee > 0);
  const withActuals = benchmarks.filter(b => b.has_actuals);
  const avgMargin = withPricing.length > 0 ? (withPricing.reduce((s, b) => s + b.margin_percentage, 0) / withPricing.length).toFixed(1) : 0;
  const totalFees = withPricing.reduce((s, b) => s + b.total_fee, 0);

  const tabs = [
    { id: "projects", label: "Project Records" },
    { id: "analytics", label: "Analytics" },
    { id: "prediction", label: "Fee Prediction" },
    { id: "calibration", label: "Calibration" },
  ];

  return (
    <div data-testid="benchmarking-page" className="space-y-8">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-100">Benchmarking & Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Historical records, variance analysis, and fee prediction</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: "Total Projects", value: benchmarks.length, color: "text-slate-100" },
          { label: "Priced", value: withPricing.length, color: "text-blue-600" },
          { label: "With Actuals", value: withActuals.length, color: "text-emerald-600" },
          { label: "Avg Margin", value: `${avgMargin}%`, color: "text-orange-600" },
          { label: "Total Fees", value: `${cur.symbol}${totalFees.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`, color: "text-slate-100" },
        ].map(k => (
          <Card key={k.label} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`bench-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k.label}</p>
              <p className={`font-mono text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-800">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === "analytics") loadAnalytics(); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? "border-orange-500 text-slate-100" : "border-transparent text-slate-500 hover:text-slate-300"}`}
            data-testid={`bench-tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── PROJECT RECORDS TAB ─── */}
      {activeTab === "projects" && (
        <Card className="bg-slate-900 border border-slate-800 shadow-sm overflow-hidden">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
            <CardTitle className="text-sm font-semibold text-slate-100 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Project Benchmarking Data</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Project</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Sector</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Status</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Est. Hours</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Fee</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Act. Hours</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Act. Fee</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 text-right">Hours Var.</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3">Outcome</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-3 w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={`bench-skeleton-${i}`}><TableCell colSpan={10}><div className="h-4 bg-slate-800 rounded animate-pulse" /></TableCell></TableRow>
                )) : benchmarks.length > 0 ? benchmarks.map(b => (
                  <TableRow key={b.project_id} className={`${expandedRow === b.project_id ? "bg-blue-500/10/30" : ""}`} data-testid={`bench-row-${b.project_id}`}>
                    <TableCell className="px-3 py-2.5">
                      <button onClick={() => setExpandedRow(expandedRow === b.project_id ? null : b.project_id)} className="flex items-center gap-1.5 text-sm font-medium text-slate-100 hover:text-blue-400">
                        {expandedRow === b.project_id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {b.name}
                      </button>
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-xs text-slate-400">{b.sector || "—"}</TableCell>
                    <TableCell className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">{b.status}</span></TableCell>
                    <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{b.estimated_hours || "—"}</TableCell>
                    <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{b.total_fee ? `${cur.symbol}${b.total_fee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—"}</TableCell>
                    <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{b.actual_total_hours || "—"}</TableCell>
                    <TableCell className="px-3 py-2.5 font-mono text-xs text-right">{b.actual_fee ? `${cur.symbol}${b.actual_fee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : "—"}</TableCell>
                    <TableCell className="px-3 py-2.5 font-mono text-xs text-right">
                      {b.hours_variance_pct != null ? (
                        <span className={`flex items-center justify-end gap-0.5 ${b.hours_variance_pct > 0 ? "text-red-600" : "text-emerald-600"}`}>
                          {b.hours_variance_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {b.hours_variance_pct > 0 ? "+" : ""}{b.hours_variance_pct}%
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="px-3 py-2.5">
                      {b.completion_status ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${b.completion_status === "On Budget" ? "bg-emerald-500/10 text-emerald-600" : b.completion_status === "Under Budget" ? "bg-blue-500/10 text-blue-600" : "bg-rose-500/10 text-red-600"}`}>{b.completion_status}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="px-3 py-2.5">
                      <button onClick={() => { setEditingId(editingId === b.project_id ? null : b.project_id); }} className="text-xs text-blue-500 hover:text-blue-400 font-medium" data-testid={`edit-actuals-${b.project_id}`}>
                        {editingId === b.project_id ? "Close" : "Record"}
                      </button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={10} className="text-center py-12 text-sm text-slate-400">No benchmarking data yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            {/* Expanded Row Detail */}
            {expandedRow && (() => {
              const b = benchmarks.find(x => x.project_id === expandedRow);
              if (!b) return null;
              const estByRole = b.estimated_hours_by_role || {};
              const actByRole = b.actual_hours_by_role || {};
              return (
                <div className="bg-slate-800/50/80 border-t border-slate-800 px-6 py-4 space-y-4" data-testid="expanded-detail">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div><span className="text-slate-500">Floor Area:</span> <span className="font-mono font-medium">{b.floor_area || 0} sqm</span></div>
                    <div><span className="text-slate-500">Floors:</span> <span className="font-mono font-medium">{b.number_of_floors || 0}</span></div>
                    <div><span className="text-slate-500">Procurement:</span> <span className="font-mono font-medium">{b.procurement_route || "—"}</span></div>
                    <div><span className="text-slate-500">Multiplier:</span> <span className="font-mono font-medium">{b.combined_multiplier || 1}x</span></div>
                  </div>
                  {b.has_actuals && Object.keys(actByRole).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Hours by Role: Estimated vs Actual</h4>
                      <div className="grid grid-cols-5 gap-3">
                        {ROLES.map(role => {
                          const est = estByRole[role]?.hours || 0;
                          const act = actByRole[role] || 0;
                          const diff = act - est;
                          return (
                            <div key={role} className="bg-slate-900 border border-slate-800 rounded p-2 text-center">
                              <p className="text-xs text-slate-500 truncate">{role.split(" ")[0]}</p>
                              <p className="font-mono text-xs mt-1">Est: {est}</p>
                              <p className="font-mono text-xs">Act: {act}</p>
                              <p className={`font-mono text-xs font-bold ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>{diff > 0 ? "+" : ""}{diff.toFixed(0)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Stage Breakdown */}
                  {b.fee_by_stage && Object.keys(b.fee_by_stage).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Estimated Fee by Stage</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {Object.entries(b.fee_by_stage).sort().map(([stage, data]) => {
                          const actStageHrs = b.actual_hours_by_stage?.[stage] || 0;
                          const estHrs = data.hours || 0;
                          const diff = actStageHrs ? actStageHrs - estHrs : null;
                          return (
                            <div key={stage} className="bg-slate-900 border border-slate-800 rounded p-2">
                              <p className="text-xs text-slate-500 truncate font-medium">{stage}</p>
                              <p className="font-mono text-xs mt-1 text-slate-300">{estHrs}h — {cur.symbol}{(data.fee || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                              {diff !== null && <p className={`font-mono text-xs font-bold ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>Act: {actStageHrs}h ({diff > 0 ? "+" : ""}{diff.toFixed(0)})</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Discipline Breakdown */}
                  {b.fee_by_discipline && Object.keys(b.fee_by_discipline).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Estimated Fee by Discipline</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(b.fee_by_discipline).sort().map(([disc, data]) => {
                          const actDiscHrs = b.actual_hours_by_discipline?.[disc] || 0;
                          const estHrs = data.hours || 0;
                          const diff = actDiscHrs ? actDiscHrs - estHrs : null;
                          return (
                            <div key={disc} className="bg-slate-900 border border-slate-800 rounded p-2">
                              <p className="text-xs text-slate-500 truncate font-medium">{disc}</p>
                              <p className="font-mono text-xs mt-1 text-slate-300">{estHrs}h — {cur.symbol}{(data.fee || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                              {diff !== null && <p className={`font-mono text-xs font-bold ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>Act: {actDiscHrs}h ({diff > 0 ? "+" : ""}{diff.toFixed(0)})</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {b.formula_version && (
                    <div><span className="text-xs text-slate-500">Formula Version:</span> <span className="font-mono text-xs text-slate-400">{b.formula_version}</span></div>
                  )}
                  {b.lessons_learned && (
                    <div><span className="text-xs font-semibold text-slate-500">Lessons Learned:</span> <span className="text-xs text-slate-400">{b.lessons_learned}</span></div>
                  )}
                </div>
              );
            })()}

            {/* Actuals Editor */}
            {editingId && (() => {
              const b = benchmarks.find(x => x.project_id === editingId);
              if (!b) return null;
              return (
                <div className="px-6 pb-4">
                  <ActualsEditor benchmark={b} onSave={(data) => saveActuals(editingId, data)} onCancel={() => setEditingId(null)} />
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ─── ANALYTICS TAB ─── */}
      {activeTab === "analytics" && (
        <div className="space-y-6">
          {/* Sector Averages */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Sector Averages</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Sector</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Projects</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Avg Fee</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Avg Hours</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Avg Margin</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Avg Bench %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sectorAvgs.length > 0 ? sectorAvgs.map(s => (
                    <TableRow key={s.sector}>
                      <TableCell className="px-4 py-2.5 text-sm font-medium">{s.sector}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.project_count}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{cur.symbol}{s.avg_fee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.avg_hours}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.avg_margin_pct}%</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.avg_benchmark_pct}%</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-400">No analytics data yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Size Benchmarks */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee per Square Metre (Size Benchmarks)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Project</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Sector</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Floor Area</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Total Fee</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">{cur.symbol}/sqm</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Hrs/sqm</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sizeBenchmarks.length > 0 ? sizeBenchmarks.map(s => (
                    <TableRow key={s.project_id}>
                      <TableCell className="px-4 py-2.5 text-sm font-medium">{s.name}</TableCell>
                      <TableCell className="px-4 py-2.5 text-xs text-slate-400">{s.sector}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.floor_area.toLocaleString()} sqm</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{cur.symbol}{s.total_fee.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right font-bold text-orange-600">{cur.symbol}{s.fee_per_sqm}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{s.hours_per_sqm}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-400">No projects with floor area data.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Variance Report */}
          {varianceReport && varianceReport.summary && varianceReport.summary.total_projects_with_actuals > 0 && (
            <Card className="bg-slate-900 border border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Variance Analysis (Estimated vs Actual)</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-800/50 rounded-lg">
                  {[
                    { l: "Total Est. Hours", v: varianceReport.summary.total_estimated_hours },
                    { l: "Total Act. Hours", v: varianceReport.summary.total_actual_hours },
                    { l: "Hours Variance", v: `${varianceReport.summary.overall_hours_variance_pct}%`, c: varianceReport.summary.overall_hours_variance_pct > 0 ? "text-red-600" : "text-emerald-600" },
                    { l: "Avg Variance", v: `${varianceReport.summary.avg_hours_variance_pct}%` },
                  ].map(({ l, v, c }) => (
                    <div key={l}><span className="text-xs text-slate-500 block">{l}</span><span className={`font-mono text-lg font-bold ${c || "text-slate-100"}`}>{v}</span></div>
                  ))}
                </div>
                {varianceReport.projects.map(p => (
                  <div key={p.project_id} className="border border-slate-800 rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{p.name} <span className="text-xs text-slate-400">({p.sector})</span></span>
                      <span className={`font-mono text-sm font-bold ${p.hours_variance_pct > 0 ? "text-red-600" : "text-emerald-600"}`}>{p.hours_variance_pct > 0 ? "+" : ""}{p.hours_variance_pct}%</span>
                    </div>
                    {p.role_variance && Object.keys(p.role_variance).length > 0 && (
                      <div className="mt-2 grid grid-cols-5 gap-2">
                        {Object.entries(p.role_variance).map(([role, rv]) => (
                          <div key={role} className="text-center bg-slate-800/50 rounded p-1.5">
                            <p className="text-xs text-slate-500 truncate">{role.split(" ")[0]}</p>
                            <p className={`font-mono text-xs font-bold ${rv.variance > 0 ? "text-red-600" : rv.variance < 0 ? "text-emerald-600" : "text-slate-500"}`}>{rv.variance > 0 ? "+" : ""}{rv.variance}h</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ─── FEE PREDICTION TAB ─── */}
      {activeTab === "prediction" && (
        <div className="space-y-6">
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold">Fee Prediction (Historical Data)</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-slate-500">Sector</Label>
                  <Input value={predFilters.sector} onChange={(e) => setPredFilters(p => ({ ...p, sector: e.target.value }))} placeholder="e.g. Commercial" className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid="pred-sector" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-slate-500">Project Type</Label>
                  <Input value={predFilters.project_type} onChange={(e) => setPredFilters(p => ({ ...p, project_type: e.target.value }))} placeholder="e.g. New Build" className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid="pred-type" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-slate-500">Floor Area (sqm)</Label>
                  <CellInput type="number" value={predFilters.floor_area} onChange={(e) => setPredFilters(p => ({ ...p, floor_area: e.target.value }))} placeholder="e.g. 5000" className="mt-1 h-8 text-sm bg-slate-900 border-slate-800" data-testid="pred-area" />
                </div>
                <Button onClick={runPrediction} className="bg-blue-600 hover:bg-blue-500/100 text-white h-8" data-testid="run-prediction-btn">
                  <Search className="w-4 h-4 mr-1" />Predict
                </Button>
              </div>

              {prediction && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-400">{prediction.message}</p>
                  {prediction.prediction ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fee Range</h4>
                        {[
                          { l: "Low", v: prediction.prediction.fee_range.low },
                          { l: "25th %ile", v: prediction.prediction.fee_range.p25 },
                          { l: "Median", v: prediction.prediction.fee_range.median, bold: true },
                          { l: "75th %ile", v: prediction.prediction.fee_range.p75 },
                          { l: "High", v: prediction.prediction.fee_range.high },
                          { l: "Average", v: prediction.prediction.fee_range.average, bold: true },
                        ].map(({ l, v, bold }) => (
                          <div key={l} className="flex justify-between"><span className="text-xs text-slate-500">{l}</span><span className={`font-mono text-sm ${bold ? "font-bold text-orange-600" : ""}`}>{cur.symbol}{v.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span></div>
                        ))}
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours Range</h4>
                        {[
                          { l: "Low", v: prediction.prediction.hours_range.low },
                          { l: "Median", v: prediction.prediction.hours_range.median, bold: true },
                          { l: "High", v: prediction.prediction.hours_range.high },
                          { l: "Average", v: prediction.prediction.hours_range.average, bold: true },
                        ].map(({ l, v, bold }) => (
                          <div key={l} className="flex justify-between"><span className="text-xs text-slate-500">{l}</span><span className={`font-mono text-sm ${bold ? "font-bold" : ""}`}>{v} hrs</span></div>
                        ))}
                        <div className="pt-2 border-t border-slate-800">
                          <div className="flex justify-between"><span className="text-xs text-slate-500">Avg Margin %</span><span className="font-mono text-sm font-bold">{prediction.prediction.avg_margin_pct}%</span></div>
                        </div>
                        {prediction.prediction.fee_per_sqm && (
                          <div className="pt-2 border-t border-slate-800">
                            <h4 className="text-xs font-semibold text-slate-500">Predicted for {predFilters.floor_area} sqm</h4>
                            <div className="flex justify-between mt-1"><span className="text-xs text-slate-500">Low</span><span className="font-mono text-sm">{cur.symbol}{prediction.prediction.fee_per_sqm.predicted_fee_low.toLocaleString("en-GB")}</span></div>
                            <div className="flex justify-between"><span className="text-xs text-slate-500">Mid</span><span className="font-mono text-sm font-bold text-orange-600">{cur.symbol}{prediction.prediction.fee_per_sqm.predicted_fee_mid.toLocaleString("en-GB")}</span></div>
                            <div className="flex justify-between"><span className="text-xs text-slate-500">High</span><span className="font-mono text-sm">{cur.symbol}{prediction.prediction.fee_per_sqm.predicted_fee_high.toLocaleString("en-GB")}</span></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-4 bg-amber-500/10 border border-amber-200 rounded-md">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-400">Insufficient historical data. Price more projects to improve predictions.</span>
                    </div>
                  )}
                </div>
              )}
              {!prediction && (
                <p className="text-sm text-slate-400 text-center py-6">Enter filters and click "Predict" to see fee ranges based on historical pricing data.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── CALIBRATION TAB ─── */}
      {activeTab === "calibration" && <CalibrationTab />}
    </div>
  );
}
